import React, { useEffect, useMemo, useState } from "react";
import { ComparisonPeriod, KpiPanelProps, resolveI18nLabel } from "./types";
import { useKpiData } from "../../../../../hooks/ChatJess/useKpiData";
import KpiCard from "./KpiCard";
import styles from "./KpiPanel.module.scss";
import { detectLanguageByTimezone } from "../../../../../utils/languageDetection";

const KpiPanel: React.FC<KpiPanelProps> = ({
  isOpen,
  onClose,
  isDarkTheme,
  isSidebarCollapsed,
  useMock,
  wsData,
}) => {
  const { data, isLoading, error, refetch, handleWsUpdate } = useKpiData(
    useMock,
    isOpen,
  );
  const [selectedPeriod, setSelectedPeriod] =
    useState<ComparisonPeriod>("previous_day");

  useEffect(() => {
    if (wsData) {
      handleWsUpdate(wsData);
    }
  }, [wsData, handleWsUpdate]);
  const lang = useMemo(() => detectLanguageByTimezone(), []);

  const periodLabels: Record<
    ComparisonPeriod,
    Record<string, string>
  > = useMemo(
    () => ({
      previous_day: { pt: "Dia", en: "Day", es: "Día" },
      previous_week: { pt: "Semana", en: "Week", es: "Semana" },
      previous_month: { pt: "Mês", en: "Month", es: "Mes" },
    }),
    [],
  );

  const panelClass = `${styles.kpiPanel} ${isOpen ? styles.open : ""} ${
    isSidebarCollapsed ? styles.sidebarCollapsed : ""
  } ${!isDarkTheme ? styles.light : ""}`;

  const formatLastUpdated = (isoDate: string): string => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <aside className={panelClass} data-testid="kpi-panel">
      <div className={styles.kpiPanelHeader}>
        <div className={styles.kpiHeaderLeft}>
          <h2 className={styles.kpiPanelTitle}>
            <i className="fas fa-chart-bar" />
            KPIs
          </h2>
          {data?.lastUpdatedAt && (
            <span className={styles.lastUpdated}>
              Atualizado às {formatLastUpdated(data.lastUpdatedAt)}
            </span>
          )}
        </div>
        <button
          className={styles.kpiPanelClose}
          onClick={onClose}
          aria-label="Fechar painel de KPIs"
        >
          <i className="fas fa-times" />
        </button>
      </div>

      <div className={styles.periodSelector}>
        {(
          [
            "previous_day",
            "previous_week",
            "previous_month",
          ] as ComparisonPeriod[]
        ).map((period) => (
          <button
            key={period}
            className={`${styles.periodButton} ${selectedPeriod === period ? styles.periodActive : ""}`}
            onClick={() => setSelectedPeriod(period)}
          >
            {periodLabels[period][lang] || periodLabels[period]["en"]}
          </button>
        ))}
      </div>

      <div className={styles.kpiPanelContent}>
        {isLoading && (
          <div className={styles.kpiLoading} data-testid="kpi-loading">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.skeletonCard}>
                <div className={styles.skeletonLine} style={{ width: "60%" }} />
                <div className={styles.skeletonLine} style={{ width: "40%" }} />
                <div className={styles.skeletonLine} style={{ width: "80%" }} />
              </div>
            ))}
          </div>
        )}

        {error && !isLoading && (
          <div className={styles.kpiError} data-testid="kpi-error">
            <i className="fas fa-exclamation-triangle" />
            <p>{error}</p>
            <button onClick={refetch} className={styles.retryButton}>
              <i className="fas fa-redo" />
              Tentar novamente
            </button>
          </div>
        )}

        {data && !isLoading && !error && (
          <>
            {data.summary &&
              (() => {
                // Find the featured KPI's trend data for the selected period
                const featuredKpi = data.categories
                  .flatMap((c) => c.kpis)
                  .find(
                    (k) =>
                      k.trends?.[selectedPeriod]?.value !== undefined &&
                      resolveI18nLabel(k.label, lang) ===
                        resolveI18nLabel(data.summary!.label, lang),
                  );
                const summaryValue =
                  featuredKpi?.trends?.[selectedPeriod]?.value ??
                  data.summary.value;
                return (
                  <div className={styles.kpiSummary}>
                    <i className="fas fa-star" />
                    <span>
                      {resolveI18nLabel(data.summary.label, lang)}:{" "}
                      {summaryValue}
                    </span>
                  </div>
                );
              })()}
            {data.categories.map((category) => (
              <div key={category.id} className={styles.kpiCategory}>
                <h3 className={styles.kpiCategoryTitle}>
                  {resolveI18nLabel(category.label, lang)}
                </h3>
                <div className={styles.kpiGrid}>
                  {category.kpis.map((kpi) => (
                    <KpiCard
                      key={kpi.id}
                      kpi={kpi}
                      isDarkTheme={isDarkTheme}
                      selectedPeriod={selectedPeriod}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {!data && !isLoading && !error && (
          <div className={styles.kpiEmpty}>
            <i className="fas fa-chart-bar" />
            <p>Aguardando dados de KPIs...</p>
            <span>Os dados aparecerão automaticamente quando disponíveis.</span>
          </div>
        )}
      </div>
    </aside>
  );
};

export default KpiPanel;
